import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { AlertTriangle, CheckCircle, Mail, Loader2 } from "lucide-react";
import { useSecureAuth } from "@/hooks/useSecureAuth";
import { validatePasswordStrength } from "@/utils/securityUtils";
import { supabase } from "@/integrations/supabase/client";
import { parseRateLimitError, rateLimitStorage } from '@/utils/rateLimitUtils';
import { RateLimitCountdown } from '@/components/ui/RateLimitCountdown';
import PersonalInfoFields from "./PersonalInfoFields";
import MarketSelector from "./MarketSelector";
import TermsCheckbox from "./TermsCheckbox";
import PlanSelectionStep from "./PlanSelectionStep";
import PaymentMethodStep from "./PaymentMethodStep";
import { toast } from "@/hooks/use-toast";
import { safeStorage } from "@/utils/safariStorage";
import { trackLead } from "@/lib/facebookPixel";

type Step = 'info' | 'markets' | 'emailVerification' | 'plan' | 'payment' | 'complete';

const EnhancedRegistrationForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>('info');
  const [formData, setFormData] = useState({
    fullName: "",
    businessName: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    phoneNumber: "",
    email: "",
    password: "",
    confirmPassword: "",
    smsOptIn: false
  });
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual' | null>(null);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const [rateLimitInfo, setRateLimitInfo] = useState<{
    type: 'ip' | 'email' | 'unknown';
    seconds: number;
  } | null>(null);
const [isResuming, setIsResuming] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('resuming') === 'true' && urlParams.get('step') === '4';
  });
  const [emailValidation, setEmailValidation] = useState<{
    isValid: boolean;
    status: 'idle' | 'checking' | 'valid' | 'invalid' | 'wrong-role';
    hasBeenValidated: boolean;
  }>({
    isValid: false,
    status: 'idle',
    hasBeenValidated: false
  });
  
  const { secureSignUp, isLoading } = useSecureAuth();
  
  const steps: { key: Step; label: string; number: number; progress: number }[] = [
    { key: 'info', label: 'Account Info', number: 1, progress: 16 },
    { key: 'markets', label: 'Markets', number: 2, progress: 33 },
    { key: 'emailVerification', label: 'Email Verification', number: 3, progress: 50 },
    { key: 'plan', label: 'Choose Plan', number: 4, progress: 66 },
    { key: 'payment', label: 'Payment', number: 5, progress: 83 },
    { key: 'complete', label: 'Complete', number: 6, progress: 100 }
  ];

  const currentStepIndex = steps.findIndex(step => step.key === currentStep);
  const currentStepData = steps[currentStepIndex];

  // Check for existing rate limit on mount
  useEffect(() => {
    const stored = rateLimitStorage.getRateLimitInfo('signup');
    if (stored.isRateLimited) {
      setRateLimitInfo({ type: 'ip', seconds: stored.remainingSeconds });
    }
  }, []);

  // Handle resume from email confirmation with onAuthStateChange listener
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const step = urlParams.get('step');
    const resuming = urlParams.get('resuming');
    
    if (resuming !== 'true' || step !== '4') return;

    let isSubscribed = true;
    let sessionHandled = false;

    // Check for error in URL hash (Supabase error format)
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const errorDescription = hashParams.get('error_description');
    if (errorDescription) {
      toast({
        title: "Verification Failed",
        description: errorDescription,
        variant: "destructive",
      });
      window.history.replaceState({}, '', '/seller/register');
      setIsResuming(false);
      return;
    }

    // Function to handle session establishment
    const handleSessionEstablished = async (session: any) => {
      if (!isSubscribed || sessionHandled) return;
      sessionHandled = true;

      // First try localStorage (same device)
      const savedData = safeStorage.getItem('godyspo_registration_data');
      if (savedData) {
        try {
          const { formData: savedFormData, selectedMarkets: savedMarkets, selectedPlan: savedPlan, timestamp } = JSON.parse(savedData);
          
          // Check if data is not expired (24 hours)
          if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
            setFormData(savedFormData);
            setSelectedMarkets(savedMarkets);
            setSelectedPlan(savedPlan);
            setCurrentStep('plan');
            window.history.replaceState({}, '', '/seller/register');
            setIsResuming(false);
            toast({
              title: "Email Verified!",
              description: "Let's continue with your registration.",
            });
            return;
          } else {
            safeStorage.removeItem('godyspo_registration_data');
          }
        } catch (error) {
          console.error('Error restoring registration data:', error);
          safeStorage.removeItem('godyspo_registration_data');
        }
      }

      // Database fallback for cross-device scenario
      try {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
        
        if (profileError) throw profileError;
        
        const { data: markets } = await supabase
          .from('user_markets')
          .select('state_id')
          .eq('user_id', session.user.id);
        
        if (profile) {
          const profileData = profile as any;
          setFormData({
            fullName: profileData.full_name || '',
            businessName: profileData.business_name || '',
            address: profileData.address || '',
            city: profileData.city || '',
            state: profileData.state || '',
            zipCode: profileData.zip_code || '',
            phoneNumber: profileData.phone_number || '',
            email: session.user.email || '',
            password: '',
            confirmPassword: '',
            smsOptIn: profileData.sms_opt_in || false
          });
          
          setSelectedMarkets(markets?.map(m => m.state_id) || []);
          setCurrentStep('plan');
          window.history.replaceState({}, '', '/seller/register');
          setIsResuming(false);
          
          toast({
            title: "Email Verified!",
            description: "Let's continue with your registration.",
          });
        }
      } catch (error) {
        console.error('Error fetching profile from database:', error);
        toast({
          title: "Unable to Resume Registration",
          description: "Please start registration again or contact support.",
          variant: "destructive",
        });
        window.history.replaceState({}, '', '/seller/register');
        setIsResuming(false);
      }
    };

    // Listen for auth state changes (handles URL token processing)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          await handleSessionEstablished(session);
        }
      }
    );

    // Also check immediately for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        handleSessionEstablished(session);
      }
    });

    // Timeout fallback - show error if no session after 8 seconds
    const timeoutId = setTimeout(() => {
      if (!sessionHandled && isSubscribed) {
        toast({
          title: "Verification Link Expired",
          description: "Please request a new verification email or start registration again.",
          variant: "destructive",
        });
        window.history.replaceState({}, '', '/seller/register');
        setIsResuming(false);
      }
    }, 8000);

    return () => {
      isSubscribed = false;
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInfoStep = () => {
    // Validate required fields
    if (!formData.fullName || !formData.email || !formData.password || !formData.confirmPassword) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    // Block if async email validation detected issues
    if (emailValidation.hasBeenValidated && 
        (emailValidation.status === 'invalid' || emailValidation.status === 'wrong-role')) {
      toast({
        title: "Email Issue",
        description: emailValidation.status === 'wrong-role' 
          ? "This email is already registered with a different account type. Please use a different email."
          : "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive",
      });
      return;
    }

    // Validate password strength
    const passwordValidation = validatePasswordStrength(formData.password);
    if (!passwordValidation.isValid) {
      toast({
        title: "Error",
        description: "Password does not meet security requirements",
        variant: "destructive",
      });
      return;
    }

    setCurrentStep('markets');
  };

  const handleStartFresh = () => {
    safeStorage.removeItem('godyspo_registration_data');
    setRateLimitError(null);
    setRateLimitInfo(null);
    setFormData({
      fullName: "",
      businessName: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      phoneNumber: "",
      email: "",
      password: "",
      confirmPassword: "",
      smsOptIn: false
    });
    setSelectedMarkets([]);
    setEmailValidation({
      isValid: false,
      status: 'idle',
      hasBeenValidated: false
    });
    setCurrentStep('info');
    window.history.replaceState({}, '', '/seller/register');
  };

  const handleMarketStep = async () => {
    // Guard: Check if form data is valid before attempting signup
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email) || !formData.password) {
      toast({
        title: "Session Expired",
        description: "Your registration data was lost. Please start again.",
        variant: "destructive",
      });
      setCurrentStep('info');
      return;
    }

    // Check if user already has a session (resuming after email verification)
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      setCurrentStep('plan');
      return;
    }

    // Markets are optional - users can add them later via Profile > Markets tab
    // Backend already handles empty arrays gracefully

    // Save state to localStorage before account creation
    safeStorage.setItem('godyspo_registration_data', JSON.stringify({
      formData,
      selectedMarkets,
      selectedPlan,
      timestamp: Date.now()
    }));

    // Create Supabase user account using secure hook
    try {
      const result = await secureSignUp(
        formData.email,
        formData.password,
        {
          user_type: 'seller',
          full_name: formData.fullName,
          business_name: formData.businessName,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zipCode,
          phone_number: formData.phoneNumber,
          sms_opt_in: formData.smsOptIn || false,
          selected_markets: selectedMarkets,
        },
        `https://godyspo.com/seller/register?step=4&resuming=true`
      );

      if (result.error) {
        throw result.error;
      }

      const data = result.data;

      if (!data.user) {
        throw new Error('User creation failed');
      }

      // Profile is now created automatically by the handle_new_user trigger
      // No need for edge function call - trigger handles all fields and markets

      // Clear any stale rate limit state since signup succeeded
      rateLimitStorage.clearRateLimit('signup');
      setRateLimitError(null);
      setRateLimitInfo(null);

      // Check if email was auto-confirmed
      if (data.user.email_confirmed_at) {
        toast({
          title: "Account Created",
          description: "Please select your plan...",
        });
        setCurrentStep('plan');
      } else {
        // Show email verification screen
        setCurrentStep('emailVerification');
      }

    } catch (error: any) {
      console.error('Account creation error:', {
        message: error?.message,
        status: error?.status,
        code: error?.code
      });
      
      // Check for server-side rate limiting (secureSignUp handles client-side)
      const rateLimitResult = parseRateLimitError(error);
      
      if (rateLimitResult.isRateLimited) {
        // Store rate limit expiry for persistence across page refresh
        const expiresAt = Date.now() + (rateLimitResult.retryAfterSeconds || 900) * 1000;
        rateLimitStorage.setRateLimited('signup', expiresAt);
        
        setRateLimitInfo({
          type: rateLimitResult.type,
          seconds: rateLimitResult.retryAfterSeconds || 900
        });
        setRateLimitError(rateLimitResult.message);
        
        toast({
          title: rateLimitResult.type === 'ip' ? "Server Rate Limit" : "Too Many Attempts",
          description: rateLimitResult.message,
          variant: "destructive",
        });
      }
      // Note: secureSignUp already shows toasts for password validation,
      // "account exists", and general errors - no need to duplicate
      
      // Clear localStorage on failure
      safeStorage.removeItem('godyspo_registration_data');
    }
  };

  const handlePlanStep = () => {
    if (!selectedPlan) {
      toast({
        title: "Error",
        description: "Please select a plan",
        variant: "destructive",
      });
      return;
    }

    // Update localStorage with selected plan
    const savedData = safeStorage.getItem('godyspo_registration_data');
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        safeStorage.setItem('godyspo_registration_data', JSON.stringify({
          ...data,
          selectedPlan,
          timestamp: Date.now()
        }));
      } catch (error) {
        console.error('Error updating registration data:', error);
      }
    }

    setCurrentStep('payment');
  };

  const handlePaymentSuccess = async () => {
    // Payment method is already set up and subscription created by PaymentMethodStep
    // Just send welcome email and complete registration
    
    // Track Lead event for Facebook Pixel
    trackLead('seller', 'Seller Registration Complete');
    
    try {
      await supabase.functions.invoke('send-welcome-email', {
        body: {
          userType: 'seller',
          email: formData.email,
          fullName: formData.fullName,
          businessName: formData.businessName || undefined
        }
      });
    } catch (emailError) {
      console.error('Welcome email failed to send:', emailError);
      // Don't block registration if email fails
    }

    // Mark pending registration as complete (fire-and-forget)
    supabase.functions.invoke('save-pending-registration', {
      body: { 
        email: formData.email, 
        user_type: 'seller',
        mark_completed: true
      }
    }).catch(() => {});

    // Clear localStorage
    safeStorage.removeItem('godyspo_registration_data');

    setCurrentStep('complete');
    setTimeout(() => {
      navigate('/dashboard');
    }, 3000);
  };

  const handleResendEmail = async () => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: formData.email,
        options: {
          emailRedirectTo: `https://godyspo.com/seller/register?step=4&resuming=true`,
        }
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Confirmation email resent! Please check your inbox.",
      });
      setRateLimitError(null);
    } catch (error: any) {
      console.error('Resend email error:', error);
      
      const rateLimitResult = parseRateLimitError(error);
      
      if (rateLimitResult.isRateLimited) {
        const expiresAt = Date.now() + (rateLimitResult.retryAfterSeconds || 300) * 1000;
        rateLimitStorage.setRateLimited('signup', expiresAt);
        
        setRateLimitInfo({
          type: rateLimitResult.type,
          seconds: rateLimitResult.retryAfterSeconds || 300
        });
        setRateLimitError(rateLimitResult.message);
        
        toast({
          title: rateLimitResult.type === 'ip' ? "Server Rate Limit" : "Too Many Attempts",
          description: rateLimitResult.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: error.message || "Failed to resend email. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const renderStep = () => {
    // Show loading state when resuming from email verification
    if (isResuming) {
      return (
        <div className="text-center py-12">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Verifying your email...</p>
        </div>
      );
    }

    switch (currentStep) {
      case 'info':
        return (
          <div className="space-y-4">
            <PersonalInfoFields 
              formData={formData} 
              handleChange={handleChange}
              onEmailValidationChange={setEmailValidation}
            />
            
            <TermsCheckbox agreeTerms={agreeTerms} setAgreeTerms={setAgreeTerms} />
            
            <Button
              onClick={handleInfoStep}
              className="w-full"
              disabled={!agreeTerms}
            >
              Continue
            </Button>
          </div>
        );

      case 'markets':
        return (
          <div className="space-y-4">
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold">Select Your Markets</h3>
              <p className="text-sm text-muted-foreground">
                Choose the states where you operate
              </p>
            </div>
            
            <MarketSelector 
              selectedMarkets={selectedMarkets} 
              setSelectedMarkets={setSelectedMarkets} 
            />
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setCurrentStep('info')}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={handleMarketStep}
                className="flex-1"
              >
                Continue
              </Button>
            </div>
          </div>
        );

      case 'emailVerification':
        return (
          <div className="text-center space-y-6">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-primary" />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-2xl font-bold">Check Your Email</h3>
              <p className="text-muted-foreground">
                We've sent a confirmation email to <strong>{formData.email}</strong>
              </p>
            </div>
            
            <div className="bg-muted rounded-lg p-4 space-y-3 text-sm">
              <p>
                Please check your inbox and click the confirmation link to continue with your registration.
              </p>
              <p className="text-muted-foreground">
                Don't see the email? Check your spam folder.
              </p>
            </div>

            <Button
              variant="outline"
              onClick={handleResendEmail}
              className="w-full"
            >
              Resend Confirmation Email
            </Button>
          </div>
        );

      case 'plan':
        return (
          <PlanSelectionStep
            selectedPlan={selectedPlan}
            onSelectPlan={setSelectedPlan}
            onNext={handlePlanStep}
          />
        );

      case 'payment':
        return selectedPlan ? (
          <PaymentMethodStep
            selectedPlan={selectedPlan}
            onSuccess={handlePaymentSuccess}
            onBack={() => setCurrentStep('plan')}
          />
        ) : null;

      case 'complete':
        return (
          <div className="text-center space-y-6">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-2xl font-bold">Welcome to GoDyspo!</h3>
              <p className="text-muted-foreground">
                Your account has been created successfully. Your 30-day free trial has started!
              </p>
            </div>
            
            <div className="bg-muted rounded-lg p-4">
              <p className="text-sm">
                Redirecting you to your dashboard in a few seconds...
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {currentStep !== 'complete' && (
        <div className="mb-8">
          <div className="flex justify-between mb-2 flex-wrap gap-2">
            {steps
              .filter(step => step.key !== 'complete')
              .map((step, index) => (
                <div
                  key={step.key}
                  className={`text-sm ${
                    index === currentStepIndex 
                      ? 'text-[#ffd700] font-semibold' 
                      : index < currentStepIndex
                      ? 'text-[#ffd700]'
                      : 'text-gray-400'
                  }`}
                >
                  {step.number}.{index === currentStepIndex ? ` ${step.label}` : ''}
                </div>
              ))}
          </div>
          <Progress value={currentStepData?.progress || 0} className="h-2" />
        </div>
      )}

      {rateLimitError && (currentStep === 'info' || currentStep === 'markets') && (
        <div className="mb-4 space-y-3">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{rateLimitError}</AlertDescription>
          </Alert>
          <Button
            onClick={handleStartFresh}
            variant="outline"
            size="sm"
            className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground"
          >
            Start Fresh with Different Email
          </Button>
        </div>
      )}

      {rateLimitInfo && (currentStep === 'info' || currentStep === 'markets') && (
        <RateLimitCountdown
          initialSeconds={rateLimitInfo.seconds}
          type={rateLimitInfo.type}
          onExpire={() => {
            setRateLimitInfo(null);
            setRateLimitError(null);
            rateLimitStorage.clearRateLimit('signup');
          }}
        />
      )}

      {renderStep()}

      {currentStep !== 'complete' && (
        <div className="text-center">
          <span className="text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/seller/login" className="text-primary hover:text-primary/90 underline-offset-4 hover:underline">
              Sign in
            </Link>
          </span>
        </div>
      )}

      {currentStep !== 'complete' && (
        <div className="text-center mt-2">
          <span className="text-sm text-muted-foreground">
            Are you a buyer?{" "}
            <Link to="/buyer/register" className="text-primary hover:text-primary/90 underline-offset-4 hover:underline">
              Register here
            </Link>
          </span>
        </div>
      )}
    </div>
  );
};

export default EnhancedRegistrationForm;